import { RunContextApp } from "framework/RCApp";
import { DAPipelineMasterQueryParams } from "types/apis/pipelineInterfaces";
import { CMSApiResponse } from "framework/http/CMSApiClient";
import { PipelineProcessComponents } from "types/apis/pipelineProcessInterfaces";
import { toast } from '../utils/toast';


// Centralized Error Handling Utility for Pipeline API
export interface ApiError {
  message: string;
  code?: string;
  status?: number;
  details?: any;
}

export interface ErrorHandlingOptions {
  showToast?: boolean;
  showAlert?: boolean;
  context?: string;
  fallbackMessage?: string;
}

export class ErrorHandler {
  /**
   * Handles API errors in a consistent way across the application
   * @param error - The error object from API response or catch block
   * @param options - Configuration options for error handling
   * @returns Formatted error message
   */
  static handleApiError(
    error: any, 
    options: ErrorHandlingOptions = {}
  ): string {
    const {
      showToast = true,
      context = 'operation',
      fallbackMessage = 'An unexpected error occurred'
    } = options;

    console.error(`${context} error:`, error);
    
    // Extract error message from different response formats
    let errorMessage = fallbackMessage;
    let errorType: 'error' | 'warning' = 'error';
    
    if (error && typeof error === 'object') {
      // Handle API error response format
      if (error.message) {
        errorMessage = error.message;
      } else if (error.error && error.error.message) {
        // Handle nested error structure
        errorMessage = error.error.message;
      } else if (error.details) {
        // Handle validation errors
        errorMessage = Array.isArray(error.details) 
          ? error.details.join(', ')
          : error.details;
      }
    } else if (typeof error === 'string') {
      errorMessage = error;
    }

    // Categorize error types for appropriate handling
    const categorizedError = this.categorizeError(errorMessage);
    errorMessage = categorizedError.message;
    errorType = categorizedError.type;

    // Show toast notification if enabled
    if (showToast) {
      this.showToast(errorMessage, errorType);
    }

    return errorMessage;
  }

  /**
   * Categorizes errors based on message content for appropriate handling
   */
  private static categorizeError(message: string): { message: string; type: 'error' | 'warning' } {
    const lowerMessage = message.toLowerCase();
    
    // Duplicate/conflict errors
    if (lowerMessage.includes('already exists') || 
        lowerMessage.includes('duplicate') || 
        lowerMessage.includes('conflict')) {
      return {
        message: `Operation failed: ${message}. Please try with different parameters.`,
        type: 'warning'
      };
    }

    // Validation errors
    if (lowerMessage.includes('validation') || 
        lowerMessage.includes('invalid') || 
        lowerMessage.includes('required') ||
        lowerMessage.includes('format')) {
      return {
        message: `Validation error: ${message}`,
        type: 'warning'
      };
    }

    // Permission/authorization errors
    if (lowerMessage.includes('permission') || 
        lowerMessage.includes('unauthorized') || 
        lowerMessage.includes('forbidden') ||
        lowerMessage.includes('access denied')) {
      return {
        message: `Access denied: ${message}`,
        type: 'error'
      };
    }

    // Network/connection errors
    if (lowerMessage.includes('network') || 
        lowerMessage.includes('connection') || 
        lowerMessage.includes('fetch')) {
      return {
        message: 'Network error: Please check your connection and try again.',
        type: 'error'
      };
    }

    // Timeout errors
    if (lowerMessage.includes('timeout') || 
        lowerMessage.includes('timed out')) {
      return {
        message: 'Request timeout: Please try again.',
        type: 'warning'
      };
    }

    // Server errors
    if (lowerMessage.includes('server error') || 
        lowerMessage.includes('internal error') ||
        lowerMessage.includes('500')) {
      return {
        message: 'Server error: Please try again later.',
        type: 'error'
      };
    }

    // Not found errors
    if (lowerMessage.includes('not found') || 
        lowerMessage.includes('404')) {
      return {
        message: `Resource not found: ${message}`,
        type: 'warning'
      };
    }

    // Rate limiting
    if (lowerMessage.includes('rate limit') || 
        lowerMessage.includes('too many requests')) {
      return {
        message: 'Too many requests: Please wait a moment and try again.',
        type: 'warning'
      };
    }

    // Default case
    return {
      message: message,
      type: 'error'
    };
  }

  /**
   * Shows appropriate toast notification based on error type
   */
  private static showToast(message: string, type: 'error' | 'warning'): void {
    switch (type) {
      case 'warning':
        toast.warning(message);
        break;
      default:
        toast.error(message);
        break;
    }
  }

  /**
   * Handles pipeline master-specific errors
   */
  static handlePipelineMasterError(error: any, operation: 'fetch' | 'create' | 'update' | 'delete' = 'fetch'): string {
    const context = `Pipeline Master ${operation}`;
    const fallbackMessage = `Pipeline Master ${operation} failed. Please try again.`;
    
    return this.handleApiError(error, {
      context,
      fallbackMessage,
      showToast: true
    });
  }
}

// Export a simple function for backward compatibility
export const handleApiError = (error: any, context?: string): string => {
  return ErrorHandler.handleApiError(error, { context });
};

export const PipelineMasterApis = {

  getDAPipelineMaster : async (rc : RunContextApp, queryParams: DAPipelineMasterQueryParams = {}) : Promise<CMSApiResponse<any>> => {
    
    const response = await rc.bffClient.getDAPipelineMaster(queryParams);
    
    // Check if the response has an error property (error object)
    if ('error' in response && response.error) {
      return {
        error: response.error
      };
    }

    // Handle successful AxiosResponse
    // The response format is {"data": []}
    const apiData = (response as any).data || [];

    // Transform the API response to match the expected format
    return {
      data: apiData, // Return the data directly since it's already in the correct format
      error: null
    };
  },

  getRecoCloneStatus : async (rc : RunContextApp, projectId: string) : Promise<CMSApiResponse<any>> => {
    try {
      const response = await rc.bffClient.getRecoCloneStatus(projectId);

      if ('error' in response && response.error) {
        return {
          error: response.error
        };
      }

      return {
        data: (response as any).data,
        error: null
      };
    } catch (error) {
      const errorMessage = ErrorHandler.handlePipelineMasterError(error, 'fetch');
      return {
        error: errorMessage
      };
    }
  },

  getPipelineComponents : async (rc : RunContextApp, projectId: string) : Promise<CMSApiResponse<any>> => {
    try {
      const response = await rc.bffClient.getPipelineComponents(projectId);
      
      // Check if the response has an error property (error object)
      if ('error' in response && response.error) {
        return {
          error: response.error
        };
      }

      // Handle successful AxiosResponse
      const apiData = (response as any).data || [];

      // Transform the API response to match the expected format
      return {
        data: apiData,
        error: null
      };
    } catch (error) {
      const errorMessage = ErrorHandler.handlePipelineMasterError(error, 'fetch');
      return {
        error: errorMessage
      };
    }
  },

  callHybridModels : async (rc : RunContextApp, projectId: string, v1Fuels: string[], v2Fuels: string[]) : Promise<CMSApiResponse<any>> => {
    try {
      const response = await rc.bffClient.callHybridModels(projectId, v1Fuels, v2Fuels);
      
      // Check if the response has an error property (error object)
      if ('error' in response && response.error) {
        return {
          error: response.error
        };
      }

      // Handle successful AxiosResponse
      const apiData = (response as any).data || {};

      // Transform the API response to match the expected format
      return {
        data: apiData,
        error: null
      };
    } catch (error) {
      const errorMessage = ErrorHandler.handlePipelineMasterError(error, 'update');
      return {
        error: errorMessage
      };
    }
  },

  callShc : async (rc : RunContextApp, projectId: string) : Promise<any> => {
    try {
      const response = await rc.bffClient.callShc(projectId);
      
      // Check if the response has an error property (error object)
      if ('error' in response && response.error) {
        return {
          error: response.error
        };
      }

      // Handle successful AxiosResponse
      const apiData = (response as any).data || {};

      // Transform the API response to match the expected format
      return {
        data: apiData,
        error: null
      };
    } catch (error) {
      const errorMessage = ErrorHandler.handlePipelineMasterError(error, 'update');
      return {
        error: errorMessage
      };
    }
  },

  // Enhanced Pipeline Process API - unified endpoint for all pipeline components
  processPipeline : async (rc : RunContextApp, projectId: string, components: PipelineProcessComponents, supportedLocale?: string | string[] | null) : Promise<CMSApiResponse<any>> => {
    try {
      const response = await rc.bffClient.processPipeline(projectId, components, supportedLocale);
      
      // Check if the response has an error property (error object)
      if ('error' in response && response.error) {
        return {
          error: response.error
        };
      }

      // Handle successful AxiosResponse
      const apiData = (response as any).data || {};

      // Transform the API response to match the expected format
      return {
        data: apiData,
        error: null
      };
    } catch (error) {
      const errorMessage = ErrorHandler.handlePipelineMasterError(error, 'update');
      return {
        error: errorMessage
      };
    }
  }

}